import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import { SearchMenuStates } from 'components/Search/type'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import React, { ChangeEventHandler, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import TextInputList from 'components/Common/Form/Input/TextInputList'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { EmptyArray } from 'lib/array/arrayUtils'
import BusinessTravellerMultiSelectContent from './BusinessTravellerMultiSelectContent'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useAppDispatch } from 'hooks/reduxHooks'
import { resetCheckoutForm } from 'actions/CheckoutActions'
import { clearFormStateSnapshot } from 'storage/checkout'
import { saveBusinessTravellerSelectedTravellerState } from 'businessTraveller/storage/businessTravellerStorage'
import useBusinessTravellerEmployeeQuery from 'businessTraveller/hooks/useEmployeeQuery'
import { pluralizeToString } from 'lib/string/pluralize'
import setSelectedBusinessTravellersAndGuests from 'actions/businessTraveller/setSelectedBusinessTravellerEmployeesAndGuests'
interface MappedStateProps {
  travellerEmployees: Array<App.BusinessTraveller.EmployeeFromMeEndpoint>
  selectedTravellerEmployeeIds?: Array<string>
  selectedTravellerGuestCount?: number
}
interface Props {
  className?: string;
  activeMenu: SearchMenuStates;
  onRoomChange: (rooms: Array<App.Occupants>) => void;
  openTravellerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void
  closeMenu: () => void
}
function BusinessTravellerSelectHotelsDesktop(props: Props & MappedStateProps) {
  const {
    className,
    activeMenu,
    travellerEmployees,
    selectedTravellerEmployeeIds,
    selectedTravellerGuestCount,
    onRoomChange,
    openTravellerMenu,
  } = props
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const appDispatch = useAppDispatch()
  const { occupancies = EmptyArray } = useContext(GlobalSearchStateContext)
  const { employees } = useBusinessTravellerEmployeeQuery()

  const setActiveMenu = useCallback((menu: SearchMenuStates) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [globalSearchDispatch])
  const handleSelectTravellerClose = useCallback(() => {
    setActiveMenu(SearchMenuStates.Closed)
  }, [setActiveMenu])
  const [searchTerm, setSearchTerm] = useState<string>('')
  const handleSearchTermChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setSearchTerm(event.currentTarget.value)
  }, [])
  const fieldValue = useMemo(() => {
    const totalTravellers = (selectedTravellerEmployeeIds?.length ?? 0) + (selectedTravellerGuestCount ?? 0)
    const adultsCount = pluralizeToString('adult', totalTravellers)
    const roomCount = pluralizeToString('room', occupancies.length)
    if (!totalTravellers) return undefined
    if (totalTravellers === 1) {
      const firstTraveller = travellerEmployees[0]
      return firstTraveller ? `${firstTraveller.firstName} ${firstTraveller.lastName}` : `${adultsCount} (${roomCount})`
    }
    return `${adultsCount} (${roomCount})`
  }, [selectedTravellerEmployeeIds, selectedTravellerGuestCount, travellerEmployees, occupancies])
  const handleSelectTravellerApply = useCallback((newState: { selectedTravellers: Array<string>, guestCount: number }) => {
    if (newState.selectedTravellers.length === 0 && newState.guestCount === 0) {
      showSnackbar('Please select at least one traveller or guest.', 'warning')
      return
    }
    // Because we are now saving the traveller form, we need to make sure its cleared when a new traveller is selected.
    appDispatch(resetCheckoutForm())
    clearFormStateSnapshot()
    // Update selected travellers
    appDispatch(setSelectedBusinessTravellersAndGuests(newState.selectedTravellers, newState.guestCount))
    onRoomChange([{ ...occupancies[0], adults: newState.selectedTravellers.length + newState.guestCount }])
    // Save to localstorage so persist on page refresh
    saveBusinessTravellerSelectedTravellerState(newState.selectedTravellers, newState.guestCount)
  }, [appDispatch, onRoomChange, occupancies])
  const localInputRef = useRef<HTMLButtonElement>(null)
  const hasEmployees = employees ? Object.values(employees).length > 1 : false
  return (
    <SearchFormFieldGroup className={className}>
      <SearchFormField
        label="Travellers"
        required
        requiredErrorMessage="Please select traveller"
        value={fieldValue}
        displayValue={fieldValue}
        placeholder="Select Traveller"
        onClick={openTravellerMenu}
        ref={localInputRef}
      />
      <DropdownList
        size="M"
        open={activeMenu === SearchMenuStates.Rooms}
        onClose={handleSelectTravellerClose}
        triggerRef={localInputRef}
        anchorRef={localInputRef}
        data-testid="traveller-select-dropdown"
        headerExtension={hasEmployees && <TextInputList
          value={searchTerm}
          type="text"
          placeholder="Search traveller name"
          onChange={handleSearchTermChange}
          endIcon={<LineSearchIcon />}
          noValidationSpacing
          autoFocus
        />}
      >
          <BusinessTravellerMultiSelectContent
            searchTerm={searchTerm}
            hotelTravellerSelection
            travellerSelectState={{
              selectedTravellers: selectedTravellerEmployeeIds ?? [],
              guestCount: selectedTravellerGuestCount ?? 0,
            }}
            setTravellerSelectState={handleSelectTravellerApply}
          />
      </DropdownList>
    </SearchFormFieldGroup>
  )
}
export default connect<MappedStateProps, {}, Props, App.State>((state) => {
  return {
    travellerEmployees: selectSelectedTravellerEmployees(state),
    selectedTravellerEmployeeIds: state.businessTraveller.selectedTravellerEmployeeIds,
    selectedTravellerGuestCount: state.businessTraveller.selectedTravellerGuestCount,
  }
})(BusinessTravellerSelectHotelsDesktop)
